export enum MfaProvider {
  None = 'none',
  MfaGauth = 'mfa-gauth',
  SmsAuth = 'sms-auth',
  EmailAuth = 'email-auth',
}

export enum AccountSecurityFlowType {
  EnableEmailMfa = 'enableEmailMfa',
  EnableAppMfa = 'enableAppMfa',
  SoftEnforceEmailMfa = 'softEnforceEmailMfa',
  ChangeEmail = 'changeEmail',
  VerifyEmail = 'verifyEmail',
}

export enum AccountSecurityVerificationCodeFlowStep {
  Initial = 'initial',
  VerifyCode = 'verifyCode',
  Completed = 'completed',
}

export enum AccountSecurityChangeEmailFlowStep {
  Initial = 'initial',
  EnterNewEmail = 'enterNewEmail',
  VerifyCode = 'verifyCode',
  Completed = 'completed',
}

export type AccountSecurityFlowDataBase = {
  form: {
    valid: boolean;
  };
};

export type AccountSecurityFlowDataWithVerificationCode = AccountSecurityFlowDataBase & {
  form: {
    verificationCode?: string;
  };
};

export type AccountSecurityEnableAppMfaFlowData = AccountSecurityFlowDataWithVerificationCode & {
  secretKey?: string;
  qrCodeVerificationUrl?: string;
};

export type AccountSecurityChangeEmailFlowData = AccountSecurityFlowDataWithVerificationCode & {
  form: {
    newEmail?: string;
  };
};

export type AccountSecurityFlowTypeToFlowDataMap = {
  [AccountSecurityFlowType.EnableEmailMfa]: AccountSecurityFlowDataWithVerificationCode;
  [AccountSecurityFlowType.EnableAppMfa]: AccountSecurityEnableAppMfaFlowData;
  [AccountSecurityFlowType.ChangeEmail]: AccountSecurityChangeEmailFlowData;
  [AccountSecurityFlowType.VerifyEmail]: AccountSecurityFlowDataWithVerificationCode;
  [AccountSecurityFlowType.SoftEnforceEmailMfa]: AccountSecurityFlowDataWithVerificationCode;
};

export enum AccountSecurityError {
  Unknown = 'unknown',
  InitiateEmailChangeInvalidEmail = 'initiateEmailChangeInvalidEmail',
  InitiateEmailChangeSameEmail = 'initiateEmailChangeSameEmail',
  Send2faEmailVerificationCode = 'send2faEmailVerificationCode',
  Send2faEmailVerificationCodeEmailAlreadyVerified = 'send2faEmailVerificationCodeEmailAlreadyVerified',
  Validate2faEmailVerificationCodeInvalidOrExpiredCode = 'validate2faEmailVerificationCodeInvalidOrExpiredCode',
}

export enum AccountSecurityActionName {
  GetMfaStatus = 'getMfaStatus',
  EnableGAuthValidated = 'enableGAuthValidated',
  ValidateGAuth = 'validateGAuth',
  Send2faEmailVerificationCode = 'send2faEmailVerificationCode',
  ConfirmEmailChange = 'confirmEmailChange',
  InitiateEmailChange = 'initiateEmailChange',
  Validate2faEmailVerificationCode = 'validate2faEmailVerificationCode',
  EnableEmailMfa = 'enableEmailMfa',
  DisableGAuth = 'disableGAuth',
  ResetFlow = 'resetFlow',
}

export type AccountSecurityActionNameToSuccessDataMap = Record<AccountSecurityActionName[number], undefined> & {
  [AccountSecurityActionName.GetMfaStatus]: { mfaProvider: MfaProvider };
  [AccountSecurityActionName.EnableGAuthValidated]: { secretKey: string; qrCodeVerificationUrl: string };
  [AccountSecurityActionName.Validate2faEmailVerificationCode]: { emailMfaSetToEnabled: boolean };
};

export type AccountSecurityActionNameToErrorMap = Record<
  AccountSecurityActionName[number],
  AccountSecurityError.Unknown
> & {
  [AccountSecurityActionName.Send2faEmailVerificationCode]:
    | AccountSecurityError.Send2faEmailVerificationCode
    | AccountSecurityError.Send2faEmailVerificationCodeEmailAlreadyVerified;
  [AccountSecurityActionName.InitiateEmailChange]:
    | AccountSecurityError.Unknown
    | AccountSecurityError.InitiateEmailChangeInvalidEmail
    | AccountSecurityError.InitiateEmailChangeSameEmail;
  [AccountSecurityActionName.Validate2faEmailVerificationCode]:
    | AccountSecurityError.Unknown
    | AccountSecurityError.Validate2faEmailVerificationCodeInvalidOrExpiredCode;
};

export type AccountSecurityFlowTypeToActionNameMap = {
  [AccountSecurityFlowType.EnableEmailMfa]:
    | AccountSecurityActionName.EnableEmailMfa
    | AccountSecurityActionName.Send2faEmailVerificationCode
    | AccountSecurityActionName.Validate2faEmailVerificationCode
    | AccountSecurityActionName.ResetFlow;
  [AccountSecurityFlowType.EnableAppMfa]:
    | AccountSecurityActionName.EnableGAuthValidated
    | AccountSecurityActionName.ValidateGAuth
    | AccountSecurityActionName.ResetFlow;
  [AccountSecurityFlowType.ChangeEmail]:
    | AccountSecurityActionName.ConfirmEmailChange
    | AccountSecurityActionName.InitiateEmailChange
    | AccountSecurityActionName.Validate2faEmailVerificationCode
    | AccountSecurityActionName.ResetFlow;
  [AccountSecurityFlowType.VerifyEmail]:
    | AccountSecurityActionName.Send2faEmailVerificationCode
    | AccountSecurityActionName.Validate2faEmailVerificationCode
    | AccountSecurityActionName.ResetFlow;
  [AccountSecurityFlowType.SoftEnforceEmailMfa]:
    | AccountSecurityActionName.EnableEmailMfa
    | AccountSecurityActionName.Send2faEmailVerificationCode
    | AccountSecurityActionName.Validate2faEmailVerificationCode
    | AccountSecurityActionName.ResetFlow;
};

export type AccountSecurityFlowTypeToFlowStepMap = Record<
  | AccountSecurityFlowType.EnableEmailMfa
  | AccountSecurityFlowType.EnableAppMfa
  | AccountSecurityFlowType.SoftEnforceEmailMfa
  | AccountSecurityFlowType.VerifyEmail,
  AccountSecurityVerificationCodeFlowStep
> &
  Record<AccountSecurityFlowType.ChangeEmail, AccountSecurityChangeEmailFlowStep>;

export type AccountSecurityMfa = {
  provider: MfaProvider;
  isInitial: boolean;
  isEnabled: boolean;
  getStatusError: boolean;
};

export type AccountSecurityAction<T extends AccountSecurityActionName = AccountSecurityActionName> = {
  name: T;
  isLoading: boolean;
  success: boolean;
  successData?: AccountSecurityActionNameToSuccessDataMap[T];
  error?: AccountSecurityActionNameToErrorMap[T];
};

export type AccountSecurityActionPayload<T extends AccountSecurityActionName> = Omit<
  AccountSecurityAction<T>,
  'success'
>;

export type AccountSecurityStandaloneActionName =
  | AccountSecurityActionName.GetMfaStatus
  | AccountSecurityActionName.DisableGAuth;

export type AccountSecurityStandaloneAction<
  T extends AccountSecurityStandaloneActionName = AccountSecurityStandaloneActionName,
> = AccountSecurityAction<T>;

export type AccountSecurityFlowStep<T extends AccountSecurityFlowType> = AccountSecurityFlowTypeToFlowStepMap[T];

export type AccountSecurityFlowActionName<T extends AccountSecurityFlowType> =
  AccountSecurityFlowTypeToActionNameMap[T];

export type AccountSecurityFlowAction<T extends AccountSecurityFlowType = AccountSecurityFlowType> =
  AccountSecurityAction<AccountSecurityFlowActionName<T>>;

export type AccountSecurityFlowData<T extends AccountSecurityFlowType> = AccountSecurityFlowTypeToFlowDataMap[T];

export type AccountSecurityFlow<T extends AccountSecurityFlowType> = {
  step: AccountSecurityFlowStep<T>;
  lastAction: AccountSecurityFlowAction<T>;
  data: AccountSecurityFlowData<T>;
};

export interface AccountSecurity {
  mfa: AccountSecurityMfa;
  lastStandaloneAction: AccountSecurityStandaloneAction;
  flow: Record<AccountSecurityFlowType, AccountSecurityFlow<AccountSecurityFlowType>>;
}
